import { FWI_CLASSIFICATIONS, FWIClassification } from '../../../utils/fwiClassification';

const lastUpdate = '2026-05-28';
export const helpTitle = 'Regional Fire Behaviour Forecast (RFBF)';

// Approximate hex → human colour name used in the legend bullets. Only the
// five canonical FWI2025 palette swatches are mapped; anything else falls
// back to the hex.
const COLOR_NAMES: Record<string, string> = {
  '#0000FF': 'Blue',
  '#00FF00': 'Green',
  '#FFFF00': 'Yellow',
  '#FFA500': 'Orange',
  '#FF0000': 'Red',
};

function colorName(hex: string): string {
  return COLOR_NAMES[hex.toUpperCase()] ?? hex;
}

function rangeLabel(c: FWIClassification): string {
  if (c.maxFWI == null) return `FWI &ge; ${c.minFWI}`;
  if (c.minFWI === 0) return `FWI &lt; ${c.maxFWI}`;
  return `${c.minFWI} &le; FWI &lt; ${c.maxFWI}`;
}

function classListHTML(): string {
  return FWI_CLASSIFICATIONS()
    .map(c => `  <li><strong>${colorName(c.color)}</strong> &mdash; ${c.label} (${rangeLabel(c)})</li>`)
    .join('\n');
}

export function buildHelpContent(): string {
  return `
<h1>${helpTitle}</h1>

<h2>Overview</h2>
<p>The Regional Fire Behaviour Forecast (RFBF) projects the current CFFDRS indices
three days into the future, station by station, using the
<strong>GFS (Global Forecast System)</strong> weather forecast. The tool plots one
marker per station for each of the next three days and shades it by the projected
<strong>FWI (Fire Weather Index)</strong> class.</p>

<h2>Reading the Map</h2>
<p>Each marker is a coloured circle with a text label of the form
<code>&lt;day&gt;-&lt;fwi&gt;</code> &mdash; for example <code>2-14</code> means the FWI
on Day 2 is 14. Colour reflects the 5-class FWI palette:</p>
<ul>
${classListHTML()}
</ul>

<h2>Controls</h2>
<ul>
  <li><strong>Day 1 / Day 2 / Day 3</strong> &mdash; Jump straight to a single forecast day.</li>
  <li><strong>Play</strong> &mdash; Cycle through Day 1 &rarr; 2 &rarr; 3 every 3 seconds.</li>
  <li><strong>Pause</strong> &mdash; Stop the animation on the current day.</li>
  <li><strong>Reset</strong> &mdash; Stop the animation and return to Day 1.</li>
  <li><strong>&times;</strong> (close) &mdash; Close the tool and remove the RFBF layers from the map.</li>
</ul>

<h2>Day 1: Forecast vs Actual</h2>
<p>Before <strong>13:30 NWT (America/Yellowknife)</strong> Day 1 shows the
morning's RFBF forecast for today. From 13:30 onward, today's actual peak
weather has been observed and Day 1 switches to the <strong>actual</strong>
current CFFDRS for each station (from the live
<code>intellifire:pg_spatial_danger</code> layer). Days 2 and 3 always show
the forecast.</p>
<p>If you leave the tool open across 13:30, close and reopen it to pick up
the actual layer.</p>

<h2>Caveats</h2>
<ul>
  <li>The forecast is <strong>only as accurate as the underlying GFS model</strong>.
      Treat day-3 values with greater uncertainty than day-1 values.</li>
  <li>Stations without enough recent weather observations to seed a CFFDRS chain
      will not appear.</li>
  <li>Markers reflect a single point-in-time snapshot &mdash; the RFBF service
      regenerates its dataset periodically. Closing and reopening the tool
      fetches the latest available forecast.</li>
</ul>

<h2>Data Pipeline</h2>
<p>RFBF data is produced by the <code>intellifire_rfbf</code> service, which seeds
each station's current CFFDRS chain with GFS forecast weather and rolls the chain
forward day by day. EM3 fetches a per-day GeoJSON view via
<code>/api/rfbf/geojson/:day</code> and renders it as a Mapbox circle + symbol
layer pair.</p>

<div class="last-update">Last Updated: ${lastUpdate}</div>
`;
}

